import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

type Platform = "shopify" | "wordpress" | "gomag" | "custom" | null;

interface Step {
  id: number;
  title: string;
  description: string;
}

const steps: Step[] = [
  { id: 1, title: "Alege Platforma", description: "Selectează CMS-ul tău" },
  { id: 2, title: "Conectare", description: "Integrează OptiRO" },
  { id: 3, title: "Verificare", description: "Confirmă conexiunea" },
];

const platforms = [
  {
    id: "shopify" as Platform,
    name: "Shopify",
    icon: "🛍️",
    description: "Instalare din App Store",
  },
  {
    id: "wordpress" as Platform,
    name: "WordPress / WooCommerce",
    icon: "📝",
    description: "Plugin oficial OptiRO",
  },
  {
    id: "gomag" as Platform,
    name: "Gomag",
    icon: "🇷🇴",
    description: "Integrare nativă pentru Gomag",
  },
  {
    id: "custom" as Platform,
    name: "Site Custom",
    icon: "💻",
    description: "Script JavaScript universal",
  },
];

const jsSnippet = `<!-- OptiRO.ai Integration -->
<script>
  (function(o,p,t,i,r,o_ai){
    o_ai=o.createElement(p);o_ai.async=1;
    o_ai.src='https://cdn.optiro.ai/tracker.js';
    o_ai.setAttribute('data-site-id','YOUR_SITE_ID');
    i=o.getElementsByTagName(p)[0];
    i.parentNode.insertBefore(o_ai,i);
  })(document,'script');
</script>`;

export function OnboardingWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>(null);
  const [siteUrl, setSiteUrl] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCopyCode = () => {
    navigator.clipboard.writeText(jsSnippet);
    toast({
      title: "Cod copiat!",
      description: "Snippet-ul a fost copiat în clipboard.",
    });
  };

  const handleVerify = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      setCurrentStep(3);
    }, 2000);
  };

  const handleComplete = () => {
    navigate("/dashboard");
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {platforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    selectedPlatform === platform.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <span className="text-3xl mb-3 block">{platform.icon}</span>
                  <h3 className="font-semibold text-foreground mb-1">
                    {platform.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {platform.description}
                  </p>
                  {selectedPlatform === platform.id && (
                    <div className="mt-3 flex items-center gap-1 text-primary text-sm font-medium">
                      <Check className="w-4 h-4" />
                      Selectat
                    </div>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                URL-ul site-ului tău
              </label>
              <Input
                placeholder="https://magazin.ro"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                className="text-lg h-12"
              />
            </div>

            {selectedPlatform === "custom" ? (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Copiază acest cod în {"<head>"}
                </label>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-x-auto">
                    {jsSnippet}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2 gap-2"
                    onClick={handleCopyCode}
                  >
                    <Copy className="w-4 h-4" />
                    Copiază
                  </Button>
                </div>
              </div>
            ) : (
              <div className="card-elevated p-6">
                <h4 className="font-semibold text-foreground mb-2">
                  Instrucțiuni pentru{" "}
                  {platforms.find((p) => p.id === selectedPlatform)?.name}
                </h4>
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                      1
                    </span>
                    Accesează panoul de administrare al platformei
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                      2
                    </span>
                    Caută "OptiRO.ai" în secțiunea de plugin-uri/aplicații
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                      3
                    </span>
                    Click pe "Instalează" și autorizează accesul
                  </li>
                </ol>
                <Button variant="outline" className="mt-4 gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Deschide Ghidul Complet
                </Button>
              </div>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="text-center py-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6"
            >
              <Check className="w-10 h-10 text-primary" />
            </motion.div>
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Conectare Reușită!
            </h3>
            <p className="text-muted-foreground mb-6">
              Site-ul tău este acum conectat la OptiRO.ai. Primul scan va începe
              în câteva minute.
            </p>
            <div className="bg-accent/50 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm text-foreground">
                <strong>Ce urmează:</strong>
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>✓ Scanare inițială completă (5-10 min)</li>
                <li>✓ Generare raport de sănătate</li>
                <li>✓ Primele optimizări automate</li>
              </ul>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    currentStep >= step.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    step.id
                  )}
                </div>
                <span className="text-xs text-muted-foreground mt-2 hidden sm:block">
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`w-16 sm:w-24 h-1 mx-2 rounded transition-colors ${
                    currentStep > step.id ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="card-elevated p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {steps[currentStep - 1].title}
            </h2>
            <p className="text-muted-foreground">
              {steps[currentStep - 1].description}
            </p>
          </div>

          <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep((s) => s - 1)}
              disabled={currentStep === 1}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi
            </Button>

            {currentStep < 3 ? (
              <Button
                onClick={() => {
                  if (currentStep === 1 && selectedPlatform) {
                    setCurrentStep(2);
                  } else if (currentStep === 2 && siteUrl) {
                    handleVerify();
                  }
                }}
                disabled={
                  (currentStep === 1 && !selectedPlatform) ||
                  (currentStep === 2 && !siteUrl) ||
                  isVerifying
                }
                className="gap-2"
              >
                {isVerifying ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full"
                    />
                    Verificare...
                  </>
                ) : (
                  <>
                    Continuă
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleComplete} variant="hero" className="gap-2">
                Mergi la Dashboard
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
